import Head from 'next/head';
import Header from '../../../components/Header';
import BlogPostCard from '../../../components/BlogPostCard';
import Sidebar from '../../../components/Sidebar';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import Hero from '../../../components/Hero';
import { useRouter } from 'next/router';

const POSTS_PER_PAGE = 6;


const formatCategory = (cat) => {
  if (!cat) return '';
  return cat
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};


export async function getStaticPaths() {
  const fs = require('fs');
  const path = require('path');

  const blogDir = path.join(process.cwd(), 'pages/blog');
  const files = fs.readdirSync(blogDir);

const totalPosts = files.filter(f =>
  f.endsWith('.js') &&
  f !== 'index.js' &&
  !f.includes('[') &&
  !f.includes(']')
).length;

  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);

  const paths = [];

  for (let i = 2; i <= totalPages; i++) {
    paths.push({ params: { page: i.toString() } });
  }

  return { paths, fallback: false };
}

export async function getStaticProps() {
  const fs = require('fs');
  const path = require('path');

  const blogDir = path.join(process.cwd(), 'pages/blog');
  const files = fs.readdirSync(blogDir);

  const posts = await Promise.all(
    files
      .filter(f =>
        f.endsWith('.js') &&
        f !== 'index.js' &&
        f !== '[page].js' &&
        !f.includes('[') &&
        !f.includes(']')
      )
      .map(async (file) => {
        try {
          const mod = await import(`../${file}`);
          const metadata = mod.metadata;

          if (!metadata) return null;

          return {
            ...metadata,
            link: `/blog/${file.replace('.js', '')}`,
          };
        } catch (e) {
          return null;
        }
      })
  );

  const allPosts = posts
    .filter(Boolean)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return {
    props: {
      allPosts,
    },
  };
}

export default function BlogPage({ allPosts }) {
  const router = useRouter();

  const page = parseInt(router.query.page || 1);

  const search = router.query.q || '';
  const category = router.query.category || '';
  const tag = router.query.tag || '';

  const filtered = allPosts.filter((post) => {
    const matchSearch =
      !search ||
      post.title.toLowerCase().includes(search.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(search.toLowerCase());

    const matchCategory = !category || post.category === category;
    const matchTag = !tag || post.tags?.includes(tag);

    return matchSearch && matchCategory && matchTag;
  });

  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);

  const start = (page - 1) * POSTS_PER_PAGE;
  const posts = filtered.slice(start, start + POSTS_PER_PAGE);

  const categories = [...new Set(allPosts.map(p => p.category).filter(Boolean))];
  const tags = [...new Set(allPosts.flatMap(p => p.tags || []).filter(Boolean))];

  const recentPosts = allPosts.slice(0, 3);

const baseTitle = "Renovation Tips & Trends Blog | Modern Build Home Remodeling";
const pageTitle = page > 1 
  ? `Page ${page} | ${baseTitle}` 
  : baseTitle;


const baseDescription =
  "Explore expert home remodeling tips, design ideas, and renovation guides from Modern Build Home Remodeling.";

const isFiltered = search || category || tag;

const formattedCategory = formatCategory(category);
const formattedTag = formatCategory(tag);

const pageDescription = isFiltered
  ? category
    ? `Page ${page}. ${formattedCategory} tips, design ideas, and expert guides from Modern Build.`
    : tag
      ? `Page ${page}. ${formattedTag} home remodeling insights, tips, and guides.`
      : search
        ? `Search results page ${page}. Showing ${filtered.length} remodeling articles matching your query.`
        : `Filtered remodeling articles page ${page}.`
  : page > 1
    ? `Page ${page} of ${totalPages}. Home remodeling tips, design ideas, and renovation guides from Modern Build.`
    : baseDescription;

  return (
    <>
<Head>
  <title>{pageTitle}</title>

  <meta name="robots" content="index, follow" />

  <meta name="title" content={pageTitle} />
  <meta
    name="description"
    content={pageDescription}
  />
  <meta name="author" content="Modern Build Home Remodeling" />
  <meta
    name="keywords"
    content="home remodeling, bathroom remodeling, kitchen renovation, adu, garage conversion, trends, tips, home improvement"
  />

  <link
    rel="canonical"
    href={`https://modernbuildremodel.com${page === 1 ? '/blog' : `/blog/page/${page}`}`}
  />

  {/* Open Graph */}
  <meta property="og:title" content={pageTitle} />
  <meta
    property="og:description"
    content={pageDescription}
  />
  <meta
    property="og:image"
    content="https://modernbuildremodel.com/hero/los-angeles-california-best-home-remodling-contractor.webp"
  />
  <meta
    property="og:url"
    content={`https://modernbuildremodel.com/blog/page/${page}`}
  />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Modern Build Home Remodeling" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={pageTitle} />
  <meta
    name="twitter:description"
    content={pageDescription}
  />
  <meta
    name="twitter:image"
    content="https://modernbuildremodel.com/hero/los-angeles-california-best-home-remodling-contractor.webp"
  />
  <meta name="twitter:site" content="@modernbuildhome" />

  {/* BLOG COLLECTION SCHEMA */}
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `Modern Build Blog - Page ${page}`,
        "url": `https://modernbuildremodel.com/blog/page/${page}`,
        "isPartOf": {
          "@type": "Blog",
          "name": "Modern Build Blog",
          "url": "https://modernbuildremodel.com/blog"
        },
        "description":
          pageDescription,
        "publisher": {
          "@type": "Organization",
          "name": "Modern Build Home Remodeling",
          "url": "https://modernbuildremodel.com"
        }
      })
    }}
  />

  {/* WEBSITE SEARCH SCHEMA */}
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Modern Build Blog",
        "url": "https://modernbuildremodel.com/blog",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://modernbuildremodel.com/blog?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      })
    }}
  />

  {/* BREADCRUMB */}
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://modernbuildremodel.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://modernbuildremodel.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `Page ${page}`,
            "item": `https://modernbuildremodel.com/blog/page/${page}`
          }
        ]
      })
    }}
  />
</Head>

      <Header />
      <Hero category="blog" />

      <section className="bg-white py-16">
        <div className="container mx-auto grid md:grid-cols-3 gap-12">

          {/* POSTS */}
          <div className="md:col-span-2 space-y-8">
            {posts.map((post, i) => (
              <BlogPostCard key={i} post={post} />
            ))}

{/* PAGINATION */}
{!search && !category && !tag && totalPages > 1 && (
  <div className="mt-10 flex items-center justify-center">
    <div className="flex items-center gap-2 bg-white p-2">

      {/* PREV */}
      <a
        href={page <= 1 ? undefined : page === 2 ? '/blog' : `/blog/page/${page - 1}`}
        className={`px-4 py-2 rounded-lg border transition
          ${page <= 1
            ? 'bg-gray-100 text-gray cursor-not-allowed border-gray'
            : 'bg-white text-black border-black hover:bg-primary hover:text-white hover:border-primary'
          }`}
        aria-disabled={page <= 1}
        tabIndex={page <= 1 ? -1 : 0}
      >
        Prev
      </a>

      {/* NUMBERS */}
      {[...Array(totalPages)].map((_, i) => {
        const p = i + 1;
        const href = p === 1 ? '/blog' : `/blog/page/${p}`;
        const isActive = p === page;

        return (
          <a
            key={p}
            href={href}
            className={`px-4 py-2 rounded-lg border transition
              ${isActive
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-black border-black hover:bg-primary hover:text-white hover:border-primary'
              }`}
          >
            {p}
          </a>
        );
      })}

      {/* NEXT */}
      <a
        href={page >= totalPages ? undefined : `/blog/page/${page + 1}`}
        className={`px-4 py-2 rounded-lg border transition
          ${page >= totalPages
            ? 'bg-gray-100 text-gray cursor-not-allowed border-gray'
            : 'bg-white text-black border-black hover:bg-primary hover:text-white hover:border-primary'
          }`}
        aria-disabled={page >= totalPages}
        tabIndex={page >= totalPages ? -1 : 0}
      >
        Next
      </a>

    </div>
  </div>
)}
          </div>

          {/* SIDEBAR */}
          <Sidebar allPosts={allPosts} />
        </div>
      </section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}